import type { ShareMix } from "@/lib/comms";
import { cn } from "@/lib/utils";

// Geometry ported 1:1 from the mockup's drawPie / drawPieLegend.
const CX = 98;
const CY = 98;
const R = 88;
const R_INNER = 52;

interface Segment {
  key: keyof ShareMix;
  label: string;
  fill: string;
  swatchClass: string;
}

const SEGMENTS: readonly Segment[] = [
  { key: "jj", label: "J&J", fill: "var(--color-cat-jj)", swatchClass: "bg-cat-jj" },
  { key: "alcon", label: "Alcon", fill: "var(--color-cat-alcon)", swatchClass: "bg-cat-alcon" },
  {
    key: "cooper",
    label: "CooperVision",
    fill: "var(--color-cat-cooper)",
    swatchClass: "bg-cat-cooper",
  },
  { key: "other", label: "Other", fill: "var(--color-cat-other)", swatchClass: "bg-cat-other" },
];

interface MixDonutProps {
  mix: ShareMix;
}

export function MixDonut({ mix }: MixDonutProps) {
  const total = SEGMENTS.reduce((sum, s) => sum + mix[s.key], 0);
  let ang = -Math.PI / 2;
  const slices = SEGMENTS.map((s) => {
    const frac = mix[s.key] / total;
    const a2 = ang + frac * Math.PI * 2;
    const large = frac > 0.5 ? 1 : 0;
    const x1 = CX + R * Math.cos(ang);
    const y1 = CY + R * Math.sin(ang);
    const x2 = CX + R * Math.cos(a2);
    const y2 = CY + R * Math.sin(a2);
    ang = a2;
    return {
      ...s,
      value: mix[s.key],
      d: `M${CX} ${CY} L${x1.toFixed(2)} ${y1.toFixed(2)} A${R} ${R} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`,
    };
  });

  return (
    <div className="flex flex-wrap items-center gap-6">
      <svg width={196} height={196} viewBox="0 0 196 196" className="shrink-0">
        {slices.map((s) => (
          <path key={s.key} d={s.d} fill={s.fill} stroke="#fff" strokeWidth={2} />
        ))}
        <circle cx={CX} cy={CY} r={R_INNER} fill="#fff" />
        <text
          x={CX}
          y={CY - 2}
          textAnchor="middle"
          fontSize={30}
          fontWeight={600}
          fill="var(--color-jj-red)"
          className="font-heading"
        >
          {mix.jj}%
        </text>
        <text
          x={CX}
          y={CY + 16}
          textAnchor="middle"
          fontSize={10}
          letterSpacing={1}
          fill="var(--color-wg-07)"
        >
          J&amp;J SHARE
        </text>
      </svg>
      <div className="min-w-[140px] flex-1 space-y-2">
        {slices.map((s) => (
          <div key={s.key} className="flex items-center gap-2.5 text-sm">
            <span className={cn("size-3 shrink-0 rounded-[4px]", s.swatchClass)} />
            <span className="text-wg-08 flex-1">{s.label}</span>
            <span className="font-semibold tabular-nums">{s.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
